import { useState } from 'react';
import { useApi } from '../../hooks/useApi.js';
import { useToast } from '../../context/ToastContext.jsx';
import { PERMISSIONS, PERMISSION_LABEL } from '../../constants/permissions.js';
import {
  adminGetRoles, adminCreateRole, adminUpdateRole, adminDeleteRole,
} from '../../api/adminRolesApi.js';
import './AdminRolesPage.css';

const EMPTY_ROLE = { name: '', displayName: '', permissions: [] };

function AdminRolesPage() {
  const { showToast } = useToast();
  const [reloadKey, setReloadKey] = useState(0);
  const [modal, setModal] = useState(null); // null | { mode: 'create' | 'edit', data }

  const { data: roles = [], loading } = useApi(() => adminGetRoles(), [reloadKey]);

  const reload = () => setReloadKey(k => k + 1);

  const handleDelete = async (role) => {
    if (role.isSystem) return;
    if (!window.confirm(`確定刪除角色「${role.displayName}」？\n（已套用此角色的使用者會失去這些權限）`)) return;
    try {
      await adminDeleteRole(role.id);
      showToast(`已刪除角色：${role.displayName}`, 'success');
      reload();
    } catch (err) {
      showToast(`刪除失敗：${err.message}`, 'error');
    }
  };

  const handleSave = async (form) => {
    try {
      if (modal.mode === 'create') {
        await adminCreateRole(form);
        showToast('角色已建立', 'success');
      } else {
        await adminUpdateRole(modal.data.id, { displayName: form.displayName, permissions: form.permissions });
        showToast('角色已更新', 'success');
      }
      setModal(null);
      reload();
    } catch (err) {
      showToast(`儲存失敗：${err.message}`, 'error');
    }
  };

  return (
    <>
      <div className="admin-page-head">
        <h1>角色權限<small>ROLES &amp; PERMISSIONS</small></h1>
      </div>

      <div className="admin-card" style={{ marginBottom: 16 }}>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 13, lineHeight: 1.7 }}>
          每個角色代表「一組可使用的後台功能」。先在這裡建立角色並勾選它能做的事，再到
          <b style={{ color: 'var(--navy)' }}>「使用者管理」</b>把角色指派給人。
          想改某人能做什麼，調整他的角色權限即可。
        </p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="admin-btn primary" onClick={() => setModal({ mode: 'create', data: { ...EMPTY_ROLE } })}>
          + 新增角色
        </button>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 220 }}>角色</th>
              <th style={{ width: 140 }}>代號</th>
              <th>權限</th>
              <th style={{ width: 130 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>載入中…</td></tr>
              : roles.length === 0
                ? <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>尚無角色</td></tr>
                : roles.map(role => (
                  <tr key={role.id}>
                    <td style={{ font: '700 14px "Noto Sans TC"', color: 'var(--navy)' }}>
                      {role.displayName}
                      {role.isSystem && <span className="role-system-tag">系統</span>}
                    </td>
                    <td style={{ font: '600 12px monospace', color: 'var(--ink-soft)' }}>{role.name}</td>
                    <td>
                      {role.permissions.length === 0
                        ? <span className="role-perm-empty">（無後台權限）</span>
                        : (
                          <div className="role-perm-chips">
                            {role.permissions.map(code => (
                              <span key={code} className="role-perm-chip">{PERMISSION_LABEL[code] || code}</span>
                            ))}
                          </div>
                        )}
                    </td>
                    <td>
                      <div className="actions">
                        <button className="admin-btn ghost sm" onClick={() => setModal({ mode: 'edit', data: role })}>
                          {role.isSystem ? '檢視' : '編輯'}
                        </button>
                        <button
                          className="admin-btn danger sm"
                          disabled={role.isSystem}
                          title={role.isSystem ? '系統角色不可刪除' : ''}
                          onClick={() => handleDelete(role)}
                        >
                          刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>

      {modal && (
        <RoleModal
          mode={modal.mode}
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}

function RoleModal({ mode, initial, onSave, onClose }) {
  const [form, setForm] = useState({
    name: initial.name || '',
    displayName: initial.displayName || '',
    permissions: [...(initial.permissions || [])],
  });
  const [submitting, setSubmitting] = useState(false);

  // 系統角色（admin/customer）僅供檢視，所有欄位鎖定
  const readOnly = !!initial.isSystem;

  const togglePerm = (code) => {
    if (readOnly) return;
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(code)
        ? f.permissions.filter(c => c !== code)
        : [...f.permissions, code],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) { onClose(); return; }
    if (!form.displayName.trim()) return;
    if (mode === 'create' && !form.name.trim()) return;
    setSubmitting(true);
    try {
      await onSave({
        name: form.name.trim(),
        displayName: form.displayName.trim(),
        permissions: form.permissions,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const title = readOnly ? '檢視角色' : (mode === 'create' ? '新增角色' : '編輯角色');

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHead}>
          <h2 style={styles.modalTitle}>{title}</h2>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="row2">
            <div className="field">
              <label>顯示名稱 *</label>
              <input
                value={form.displayName}
                onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
                placeholder="主題編輯員"
                disabled={readOnly}
              />
            </div>
            <div className="field">
              <label>代號（英數，建立後不可改）{mode === 'create' ? ' *' : ''}</label>
              <input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="theme_editor"
                disabled={readOnly || mode === 'edit'}
              />
            </div>
          </div>

          <div className="field">
            <label>這個角色能做什麼</label>
            <div className="perm-grid">
              {PERMISSIONS.map(p => {
                const on = form.permissions.includes(p.code);
                return (
                  <label key={p.code} className={`perm-item${on ? ' on' : ''}${readOnly ? ' locked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={readOnly}
                      onChange={() => togglePerm(p.code)}
                    />
                    <span className="perm-item-text">
                      <b>{p.label}</b>
                      <small>{p.desc}</small>
                    </span>
                  </label>
                );
              })}
            </div>
            {readOnly && (
              <p style={{ margin: '10px 0 0', color: 'var(--ink-soft)', fontSize: 12 }}>
                系統角色不可更改。
              </p>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="admin-btn ghost" onClick={onClose}>
              {readOnly ? '關閉' : '取消'}
            </button>
            {!readOnly && (
              <button type="submit" className="admin-btn primary" disabled={submitting}>
                {submitting ? '儲存中…' : '儲存'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// 沿用 AdminSettingsPage 的 Modal 樣式
const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(14, 28, 64, 0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'var(--white)',
    borderRadius: 14,
    padding: '28px 32px',
    width: '90%',
    maxWidth: 600,
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 8px 40px rgba(14,28,64,0.18)',
  },
  modalHead: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 24,
  },
  modalTitle: {
    font: '800 20px "Noto Sans TC", sans-serif',
    color: 'var(--navy)', margin: 0,
  },
  closeBtn: {
    background: 'none', border: 'none',
    font: '600 18px Inter', color: 'var(--ink-soft)',
    cursor: 'pointer', padding: '4px 8px',
  },
};

export default AdminRolesPage;
